import React, { useState, useEffect } from 'react';
import { Button, Card, Table, Space, Modal, Form, Input, message, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import axiosInstance from '../../../axios/axios-instance';
import { Link } from 'react-router-dom';

interface SalonTranslationKey {
  id: number;
  ui_key: string;
  value: string;
}

interface TranslationDetail {
  ui_id: number;
  ui_key: string;
  default_value: string;
  language_id: number;
  language_code: string;
  translation_id: number | null;
  translation_value: string;
}

interface PaginationData {
  current_page: number;
  from: number;
  to: number;
  last_page: number;
  per_page: number;
  total_page: number;
  total: number;
}

const SalonTranslation: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingDetails, setEditingDetails] = useState<TranslationDetail[]>([]);
  const [form] = Form.useForm();
  const [keys, setKeys] = useState<SalonTranslationKey[]>([]);
  const [editForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [filteredKeys, setFilteredKeys] = useState<SalonTranslationKey[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    current_page: 0,
    from: 1,
    to: 1,
    last_page: 1,
    per_page: 20,
    total_page: 1,
    total: 0
  });

  const fetchKeys = async (page: number = 0, search: string = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page.toString());
      
      let endpoint = 'admin/setting/owner/multi-lang/keys';
      if (search) {
        endpoint = 'admin/setting/owner/multi-lang/search';
        params.append('search', search);
      }
      
      const response = await axiosInstance.get(`${endpoint}?${params.toString()}`);
      const { data, ...paginationData } = response.data;
      setKeys(data);
      setFilteredKeys(data);
      setPagination(paginationData);
    } catch (error) {
      message.error('Failed to fetch translation keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, []);

  const handleTableChange = (pagination: any) => {
    fetchKeys(pagination.current - 1, searchText); // API uses 0-based indexing
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    fetchKeys(0, value); // Reset to first page when searching
  };

  const handleDelete = async (record: SalonTranslationKey) => {
    Modal.confirm({
      title: 'Delete Confirmation',
      icon: <ExclamationCircleOutlined />,
      content: `You are going to delete the Key \`${record.ui_key}\`, are you sure?`,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await axiosInstance.delete(`admin/setting/owner/multi-lang/${record.id}`);
          message.success(`Key \`${record.ui_key}\` has been deleted`);
          fetchKeys(); // Refresh the list
        } catch (error) {
          message.error('Failed to delete key');
        }
      },
    });
  };

  const handleTranslationSave = async () => {
    try {
      setLoading(true);
      const values = await editForm.validateFields();
      
      // Get the ui_id from the first item since it's same for all
      const ui_id = editingDetails[0]?.ui_id;
      
      const requests = Object.keys(values)
        .filter(key => key.startsWith('translation_'))
        .map(key => {
          const [, languageId] = key.split('_');
          return {
            app_ui_id: ui_id,
            language_id: parseInt(languageId),
            value: values[key]
          };
        });

      await axiosInstance.post(`admin/setting/owner/multi-lang/${ui_id}`, {
        ui_key: values.ui_key,
        value: values.default_value,
        requests
      });

      message.success('Translations updated successfully');
      setIsEditModalVisible(false);
      fetchKeys(); // Refresh the main list
    } catch (error) {
      message.error('Failed to update translations');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (record: SalonTranslationKey) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`admin/setting/owner/multi-lang/${record.id}`);
      const { key, translations } = response.data.data;
      setEditingDetails(translations);
      
      // Set initial form values
      const initialValues: { [key: string]: string } = {
        ui_key: key.ui_key,
        default_value: key.value
      };
      
      // Add translation values
      translations.forEach((detail: TranslationDetail) => {
        initialValues[`translation_${detail.language_id}`] = detail.translation_value;
      });
      
      editForm.setFieldsValue(initialValues);
      setIsEditModalVisible(true);
    } catch (error) {
      message.error('Failed to fetch translation details');
    } finally {
      setLoading(false);
    }
  };

  const detailColumns = [
    {
      title: 'Language',
      dataIndex: 'language_code',
      key: 'language_code',
      width: '20%',
    },
    {
      title: 'Default Value',
      dataIndex: 'default_value',
      key: 'default_value',
      width: '30%',
    },
    {
      title: 'Translation',
      dataIndex: 'translation_value',
      key: 'translation_value',
      width: '50%',
      render: (_: string, record: TranslationDetail) => (
        <Form.Item
          name={`translation_${record.language_id}`}
          style={{ margin: 0 }}
        >
          <Input.TextArea
            rows={2}
            placeholder={`Enter ${record.language_code} translation`}
          />
        </Form.Item>
      ),
    },
  ];

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '10%',
      align: 'center' as const
    },
    {
      title: 'UI Key',
      dataIndex: 'ui_key',
      key: 'ui_key',
      width: '25%',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: '45%',
      render: (value: string) => (
        <div style={{ 
          maxHeight: '3em', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          lineHeight: '1.5em'
        }}>
          {value}
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      align: 'center' as const,
      render: (_: any, record: SalonTranslationKey) => (
        <Space split={<Divider type="vertical" />}>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button 
            type="link" 
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const handleAddKey = () => {
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleSubmit = async (values: { uiKey: string; value: string }) => {
    try {
      setLoading(true);
      const payload = {
        ui_key: values.uiKey,
        value: values.value
      };
      
      const response = await axiosInstance.post('admin/setting/owner/multi-lang/keys', payload);
      setIsModalVisible(false);
      message.success(`Key \`${values.uiKey}\` is created successfully`);
      
      // Fetch the new list first
      await fetchKeys();
      
      // Then open edit modal for the new key
      handleEdit({ 
        id: response.data.data.id, 
        ui_key: response.data.data.ui_key, 
        value: response.data.data.value 
      });
      
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to add key';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="Salon Translation Keys">
      <div style={{ 
        marginBottom: 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddKey}
        >
          Add Key
        </Button>
        <Input.Search
          placeholder="Search UI keys..."
          allowClear
          style={{ width: 300 }}
          onSearch={handleSearch}
          onChange={(e) => setSearchText(e.target.value)}
          value={searchText}
          enterButton
        />
      </div>

      <Table<SalonTranslationKey>
        loading={loading}
        columns={columns}
        dataSource={filteredKeys}
        rowKey="id"
        pagination={{
          current: pagination.current_page + 1, // Convert 0-based to 1-based
          pageSize: pagination.per_page,
          total: pagination.total,
          showSizeChanger: false, // Hide page size changer
          showQuickJumper: false, // Hide quick jumper
          showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
        }}
        onChange={handleTableChange}
      />

      <Modal
        title="Edit Translation"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        maskClosable={false}
        footer={[
          <Button key="cancel" onClick={() => setIsEditModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={loading}
            onClick={handleTranslationSave}
          >
            Save
          </Button>,
        ]}
        width={800}
      >
        <Form
          form={editForm}
          layout="vertical"
        >
          <div style={{ 
            marginBottom: 16, 
            padding: '16px',
            background: 'var(--app-bg)',
            borderRadius: '6px',
            border: '1px solid var(--app-border-light)'
          }}>
            <Form.Item
              name="ui_key"
              label="UI Key"
              style={{ marginBottom: 16 }}
            >
              <Input placeholder="Enter UI key" />
            </Form.Item>
            <Form.Item
              name="default_value"
              label="Default Value"
              style={{ margin: 0 }}
            >
              <Input.TextArea 
                rows={3}
                placeholder="Enter default value"
              />
            </Form.Item>
          </div>
          <Table
            columns={detailColumns}
            dataSource={editingDetails}
            rowKey="language_id"
            pagination={false}
          />
        </Form>
      </Modal>

      <Modal
        title="Add Translation Key"
        open={isModalVisible}
        onOk={form.submit}
        onCancel={() => setIsModalVisible(false)}
        maskClosable={false}
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="uiKey"
            label="UI Key"
            extra="This key will be used in the App to map with translations for each language"
            rules={[{ required: true, message: 'Please input the UI key!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="value"
            label="Value"
            extra="This value will be used as default text if there is no language setting"
            rules={[{ required: true, message: 'Please input the value!' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default SalonTranslation; 